/* eslint-disable max-lines-per-function */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {TrackingService} from 'extensions/hawksearch';
import 'extensions/ui-components/x-banner';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-search-pagination';
import {BreakpointLabels, BreakpointMixin} from 'extensions/ui-mixins';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import styles from './product-listing.component.scss?as=stylesheet';

export class ProductListing extends MobxReactionUpdate(BreakpointMixin(LitElement)) {

	static get styles () {
		return [styles];
	}

	static get properties () {
		return {
			apiEndpoint: {
				attribute: 'api-endpoint',
				type: String
			},
			compareList: {
				type: Object
			},
			compareNoResultsMessage: {
				attribute: 'compare-no-results-message',
				type: String
			},
			currentDevice: {
				type: String
			},
			listType: {
				attribute: 'list-type',
				type: String
			},
			resultsNoResultsMessage: {
				attribute: 'results-no-results-message',
				type: String
			},
			service: {
				state: true,
				type: Object
			},
			store: {
				state: true,
				type: Object
			}
		};
	}

	constructor () {
		super();

		/**
		 * @type {TrackingService}
		 */
		this.trackingService = new TrackingService({
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID
		});

		/**
		 * @type {string}
		 */
		this.currentDevice = '';
	}

	/**
	 * @param {string} zoneName
	 */
	getZoneItems (zoneName) {
		return this.service?.store?.state?.contentZones?.[zoneName]?.items || void 0;
	}

	/**
	 * BreakpointMixin callback
	 * @param {number} index
	 * @param {boolean} matches
	 */
	handleBreakpoint (index) {
		this.currentDevice = BreakpointLabels[index];
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').ContentType} banner
	 */
	handleBannerClick (banner) {
		if (banner.trackingEnabled) {
			this.trackingService.trackBannerClick(banner.id, banner.campaignId, this.service?.store?.state?.trackingId);
		}
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').ContentType} banner
	 */
	handleBannerLoad (banner) {
		if (banner.trackingEnabled) {
			this.trackingService.trackBannerImpression(banner.id, banner.campaignId, this.service?.store?.state?.trackingId);
		}
	}

	/**
	 * @param {string} zoneName
	 */
	renderContentZone (zoneName) {

		/**
		 * @type {import('extensions/hawksearch/services/types').ContentZoneItem[]}
		 */
		const items = this.getZoneItems(zoneName);
		if (!items || this.service?.store?.state?.pagination?.page > 1) {
			return nothing;
		}

		const content = [];

		for (const item of items) {
			// eslint-disable-next-line array-element-newline
			for (const [device, value] of Object.entries(item)) {
				if (device !== this.currentDevice) {
					continue;
				}

				switch (value.type) {
					case 'image':
						content.push(html`<x-banner
								@click="${() => this.handleBannerClick(value)}"
								@load="${() => this.handleBannerLoad(value)}"
								class="zone-device-${device}"
								device="${device}"
								url="${value.link?.url}"
								target="${value.link?.target}"
								image-url="${value.image?.url}"
								image-alt="${value.image?.altText}"
								image-title="${value.image?.title}"
								image-height="${value.image?.height}"
								image-width="${value.image?.width}"
							></x-banner>`);
						break;
					default:
						break;
				}
			}
		}

		return html`<div class="zone zone-${zoneName}">${content}</div>`;
	}

	renderInlineBanner (content) {
		return html`
			<div class="o-layout__item setting-product-listing--item inline-banner">${this.renderContentZone(content.name)}</div>
		`;
	}

	handlePaginationChange (e) {
		// eslint-disable-next-line no-extra-parens
		const [target] = e.path || (e.composedPath && e.composedPath()) || [];

		this.service.paginate(target.value);

		// Scroll to top after making pagination changes
		window.scrollTo({
			behavior: 'smooth',
			left: 0,
			top: 0
		});
	}

	renderPagination () {
		return html`
			<x-search-pagination
				@change="${this.handlePaginationChange}"
				?first="${this.service?.store?.state?.pagination?.displayFirstLink}"
				?last="${this.service?.store?.state?.pagination?.displayLastLink}"
				max-pages="3"
				value="${this.service?.store?.state?.pagination?.page}"
				total-pages="${this.service?.store?.state?.pagination?.totalPages}"
			></x-search-pagination>
		`;
	}

	renderNoResults () {
		return html`
			<rb-no-results message="${this.resultsNoResultsMessage}"></rb-no-results>
		`;
	}

	renderNoCompare () {
		return html`
			<rb-no-results message="${this.compareNoResultsMessage}"></rb-no-results>
		`;
	}

	renderLoading () {
		return html`<x-loader class="loader" aspect-ratio-desktop="5/3" aspect-ratio-tablet="5/3" aspect-ratio="5/5"></x-loader>`;
	}

	renderMain () {
		if (!this.service?.store?.state?.results?.length && this.service?.store?.loading) {
			return this.renderLoading();
		}

		return html`
			<rb-facet-list
				.store="${this.store}"
				screen-size="${this.store?.screenSize}"
				.service="${this.service}"
				.facets="${this.service?.store?.state?.facets}"
				.selectedFacets="${this.service?.store?.state?.selectedFacets}"
				class="product-listing--facets"
				api-endpoint="${this.apiEndpoint}"
			>
			</rb-facet-list>

			<div class="product-listing--main">
				<rb-list-controls
					.service="${this.service}"
					.store="${this.store}"
					.pagination="${this.service?.store?.state?.pagination}"
					.sorting="${this.service?.store?.state?.sorting}"
					list-type="${this.listType}" class="product-listing--list-controls product-listing--list-control--${this.listType}"
				></rb-list-controls>

				${this.renderMainElement()}

				${this.compareActive ? nothing : html`<div class="product-listing--pagination">${this.renderPagination()}</div>`}
			</div>
		`;
	}

	renderMainElement () {
		if (this.compareActive) {
			if (!this.compareList?.length) {
				return this.renderNoCompare();
			}
			return this.renderCompare();
		}

		if (!this.service?.store?.state?.results?.length && !this.service?.store?.loading) {
			return this.renderNoResults();
		}

		return this.renderResults();
	}

	render () {
		const classes = {
			'product-listing': true,
			'product-listing--loading': this.service?.store?.state?.results?.length && this.service?.store?.loading
		};

		return html`
			<div class="${classMap(classes)}">
				${this.renderMain()}
			</div>
		`;
	}
};

